import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@heroui/react';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { onAppEvent } from '@/lib/appEvents';
import { getSocket } from '@/lib/socket';
import { useDataStore } from '@/stores/dataStore';
import { useAuthStore } from '@/stores/authStore';
import { LoadError } from '@/components/ui/LoadError';
import { CloseIcon, ReplyIcon, PaperclipIcon, SendIcon } from '@/components/icons';
import { releaseSlots, useComposerUploads } from '@/components/contentPane/useComposerUploads';
import { sendQueue } from '@/components/contentPane/composerQueue';
import { restoreInOrder, retryNonce } from '@/components/contentPane/composerSend';
import { generateClientNonce as newNonce } from '@bunker/shared/api';
import { collectReadyAttachments, hasUploadsInFlight } from '@/components/contentPane/composerMessage';
import { applyReaction } from '@/lib/reactionToggle';
import { CommentRow } from '@/components/thread/CommentRow';
import type { Message } from '@/types/api';

/**
 * Telegram/Discord-style discussion panel — a right-side panel (full-screen on
 * mobile) that opens via the `thread:open` app event. Shows the root message
 * pinned at the top with a "Начало обсуждения" divider, the comments as a chat
 * below, and a composer at the bottom. Appends live comments from the
 * `thread:comment` socket bridge. The root author (or a mod) can close it.
 */
export function ThreadPanel() {
  const { t } = useTranslation();
  const [rootId, setRootId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [comments, setComments] = useState<Message[]>([]);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const myId = useAuthStore((s) => s.user?.id ?? null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    pendingUploads,
    startUploads,
    cancelUpload,
    clear: clearUploads,
    detach: detachUploads,
    restore: restoreUploads,
  } = useComposerUploads();
  // Which thread is open right now — a queued comment that fails gives its
  // text back only if the user is still in that thread.
  const openRootRef = useRef<string | null>(null);
  openRootRef.current = rootId;
  // Latest field value for hand-backs, which run outside render.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // Text failed comments already put back in this run (keeps send order).
  const restoredRef = useRef<string | null>(null);
  // A failed comment handed back unchanged into an empty field: resending it
  // as is reuses its nonce, so a comment whose answer was lost is not doubled.
  const retryRef = useRef<{ content: string; uploadIds: string[]; nonce: string } | null>(null);

  useEffect(() => onAppEvent('thread:open', ({ messageId }) => {
    setRootId(messageId);
    setComments([]);
    setDraft('');
    restoredRef.current = null;
    retryRef.current = null;
    setReplyingTo(null);
    clearUploads();
    // clearUploads is stable enough for one-shot reset; avoid re-subscribing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // Append live comments for this root as they arrive over the socket.
  useEffect(() => {
    if (!rootId) return;
    return onAppEvent('thread:comment', (p) => {
      if (p.rootId !== rootId) return;
      setComments((prev) =>
        prev.some((c) => c.id === p.comment.id) ? prev : [...prev, p.comment],
      );
    });
  }, [rootId]);

  // Live edits / deletes of comments (the channel-room message:update /
  // message:delete events). Root updates carry threadRootId === null and are
  // ignored here (the root is read reactively from the store above).
  useEffect(() => {
    if (!rootId) return;
    const socket = getSocket();
    const onUpdate = (m: Message) => {
      if (m.threadRootId !== rootId) return;
      setComments((prev) => prev.map((c) => (c.id === m.id ? m : c)));
    };
    const onDelete = (p: { id: string }) => {
      setComments((prev) => prev.filter((c) => c.id !== p.id));
    };
    const onReactAdd = (p: { messageId: string; userId: string; emoji: string }) => {
      setComments((prev) =>
        prev.map((c) =>
          c.id === p.messageId
            ? { ...c, reactions: applyReaction(c.reactions, p.userId, p.emoji, 1) }
            : c,
        ),
      );
    };
    const onReactRemove = (p: { messageId: string; userId: string; emoji: string }) => {
      setComments((prev) =>
        prev.map((c) =>
          c.id === p.messageId
            ? { ...c, reactions: applyReaction(c.reactions, p.userId, p.emoji, -1) }
            : c,
        ),
      );
    };
    socket.on('message:update', onUpdate);
    socket.on('message:delete', onDelete);
    socket.on('reaction:add', onReactAdd);
    socket.on('reaction:remove', onReactRemove);
    return () => {
      socket.off('message:update', onUpdate);
      socket.off('message:delete', onDelete);
      socket.off('reaction:add', onReactAdd);
      socket.off('reaction:remove', onReactRemove);
    };
  }, [rootId]);

  // Esc closes the panel.
  useEffect(() => {
    if (!rootId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setRootId(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [rootId]);

  const open = rootId !== null;
  const query = useQuery({
    queryKey: ['thread', rootId],
    queryFn: () => api.listThreadComments(rootId!),
    enabled: open,
  });

  // Reactive root lookup across loaded channels so a live message:update
  // (close / reply count) is reflected here.
  const root = useDataStore((s) => {
    if (!rootId) return null;
    for (const list of Object.values(s.messagesByChannel)) {
      const hit = list.find((m) => m.id === rootId);
      if (hit) return hit;
    }
    return null;
  });

  // Seed the comment list from the fetch; live socket events mutate it after.
  useEffect(() => {
    if (query.data) setComments(query.data.comments);
  }, [query.data]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [comments.length]);

  if (!open) return null;

  const closed = root?.threadClosed === true;
  const canClose = !!root && root.author.id === myId;

  function close() {
    setRootId(null);
  }

  async function send() {
    const content = draft.trim();
    const ready = collectReadyAttachments(pendingUploads);
    // Allow an attachment-only comment (blank body) — server accepts a space.
    if ((!content && ready.length === 0) || !rootId) return;
    if (hasUploadsInFlight(pendingUploads)) return; // wait for uploads to finish
    const target = rootId;
    const reply = replyingTo;
    const slots = pendingUploads;
    const uploadIds = ready.map((a) => a.id);
    const nonce = retryNonce(retryRef.current, content, uploadIds) ?? newNonce();
    retryRef.current = null;
    restoredRef.current = null;
    const sent = { content, uploadIds, nonce, slots, reply };
    setDraft('');
    setReplyingTo(null);
    detachUploads();
    // Same app-wide queue as channel messages: a second Enter while the first
    // comment is on its way queues behind it instead of being dropped.
    // A failure stops this thread's line: comments queued behind it come
    // back too (cancel), in send order, instead of overtaking it.
    await sendQueue.enqueue(`thread:${target}`, {
      kind: 'task',
      run: async () => {
        try {
          await api.postThreadComment(target, {
            content: content || ' ',
            ...(uploadIds.length > 0 ? { uploadIds } : {}),
            ...(reply ? { replyToId: reply.id } : {}),
            clientNonce: nonce,
          });
          releaseSlots(slots);
          void query.refetch();
          return true;
        } catch (err) {
          toast.error(...errToToast(err, t('prof.thread.sendFailed')));
          giveBack(target, sent);
          return false;
        }
      },
      cancel: () => giveBack(target, sent),
    });
  }

  /** Put a comment that did not go out back into the field (if its thread
   *  is still open), after any given back before it. */
  function giveBack(
    target: string,
    sent: { content: string; uploadIds: string[]; nonce: string; slots: typeof pendingUploads; reply: Message | null },
  ): void {
    if (openRootRef.current !== target) {
      releaseSlots(sent.slots);
      return;
    }
    const cur = draftRef.current;
    retryRef.current = cur.trim()
      ? null
      : { content: sent.content, uploadIds: sent.uploadIds, nonce: sent.nonce };
    // One-line field: parts are joined with a space.
    const next = restoreInOrder(cur, restoredRef.current, sent.content, ' ');
    restoredRef.current = next.restored;
    draftRef.current = next.draft;
    setDraft(next.draft);
    restoreUploads(sent.slots);
    if (sent.reply) setReplyingTo((r) => r ?? sent.reply);
  }

  function startReply(comment: Message) {
    setReplyingTo(comment);
    inputRef.current?.focus();
  }

  async function react(comment: Message, emoji: string) {
    if (!myId) return;
    const had = comment.reactions.some((r) => r.emoji === emoji && r.users.includes(myId));
    const delta = had ? -1 : 1;
    // Optimistic; the socket echo re-applies idempotently.
    setComments((prev) =>
      prev.map((c) =>
        c.id === comment.id
          ? { ...c, reactions: applyReaction(c.reactions, myId, emoji, delta) }
          : c,
      ),
    );
    try {
      if (had) await api.removeReaction(comment.id, emoji);
      else await api.addReaction(comment.id, emoji);
    } catch (err) {
      toast.error(...errToToast(err, t('prof.thread.reactFailed')));
      void query.refetch();
    }
  }

  async function toggleClosed() {
    if (!rootId) return;
    try {
      await api.setThreadClosed(rootId, !closed);
    } catch (err) {
      toast.error(...errToToast(err, t('prof.thread.toggleFailed')));
    }
  }

  async function deleteComment(id: string) {
    setComments((prev) => prev.filter((c) => c.id !== id)); // optimistic
    try {
      await api.deleteMessage(id);
    } catch (err) {
      toast.error(...errToToast(err, t('prof.thread.deleteFailed')));
      void query.refetch();
    }
  }

  return (
    <>
      {/* Click-outside catcher (closes the panel). */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={close} aria-hidden />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-full flex-col bg-surface shadow-2xl md:max-w-[420px]">
        <header className="flex h-14 shrink-0 items-center gap-2 px-4">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground">{t('prof.thread.title')}</div>
            {root && (root.threadReplyCount ?? 0) > 0 && (
              <div className="text-[11px] text-muted">
                {t('prof.thread.commentCount', { count: root.threadReplyCount ?? 0 })}
              </div>
            )}
          </div>
          {canClose && (
            <button
              type="button"
              onClick={() => void toggleClosed()}
              className="rounded-md px-2 py-1 text-xs text-muted transition-colors hover:bg-default/50 hover:text-foreground"
            >
              {closed ? t('prof.thread.open') : t('prof.thread.close')}
            </button>
          )}
          <button
            type="button"
            aria-label={t('prof.thread.closePanel')}
            onClick={close}
            className="rounded-md p-1.5 text-muted transition-colors hover:bg-default/50 hover:text-foreground"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </header>

        {/* Chat-style: content sticks to the BOTTOM (next to the composer)
            via mt-auto, so a short thread doesn't strand the comments at the
            top with a big gap above the input. Overflows scroll up normally. */}
        <div className="scrollbar-thin flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
          <div className="mt-auto flex flex-col gap-3">
          {root && (
            <div className="rounded-lg border border-separator bg-default/30 p-3">
              <CommentRow message={root} myId={myId} />
            </div>
          )}

          <div className="flex items-center gap-3 py-1">
            <div className="h-px flex-1 bg-divider" />
            <span className="text-[11px] uppercase tracking-wide text-muted">{t('prof.thread.start')}</span>
            <div className="h-px flex-1 bg-divider" />
          </div>

          {query.isLoading ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : query.isError ? (
            // A failed thread fetch used to read as "Комментариев пока нет".
            <LoadError error={query.error} onRetry={() => void query.refetch()} />
          ) : comments.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted">
              {t('prof.thread.empty')}
            </p>
          ) : (
            comments.map((c) => (
              <CommentRow
                key={c.id}
                message={c}
                myId={myId}
                onReply={!closed ? () => startReply(c) : undefined}
                onReact={!closed ? (emoji) => void react(c, emoji) : undefined}
                onDelete={c.author.id === myId ? () => void deleteComment(c.id) : undefined}
              />
            ))
          )}
          <div ref={bottomRef} />
          </div>
        </div>

        <div className="shrink-0 p-3">
          {closed ? (
            <p className="text-center text-xs text-muted">{t('prof.thread.closedNotice')}</p>
          ) : (
            <>
              {replyingTo && (
                <div className="mb-2 flex items-center gap-2 rounded-md bg-default/40 px-2.5 py-1.5 text-xs">
                  <ReplyIcon className="h-3.5 w-3.5 shrink-0 text-accent" />
                  <span className="min-w-0 flex-1 truncate text-muted">
                    {t('prof.thread.replyTo')}{' '}
                    <span className="text-foreground/90">
                      {replyingTo.author.displayName || replyingTo.author.username}
                    </span>
                    : {replyingTo.content}
                  </span>
                  <button
                    type="button"
                    aria-label={t('prof.thread.cancelReply')}
                    onClick={() => setReplyingTo(null)}
                    className="shrink-0 rounded p-0.5 text-muted hover:text-foreground"
                  >
                    <CloseIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {pendingUploads.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {pendingUploads.map((u) => {
                    const pct = u.size > 0 ? Math.round((u.loaded / u.size) * 100) : 0;
                    const uploading = u.status === 'uploading' || u.status === 'preparing';
                    return (
                      <div
                        key={u.fileId}
                        className={
                          'group/upl relative h-14 w-14 overflow-hidden rounded-lg ' +
                          (u.status === 'error' ? 'bg-danger/15' : 'bg-default/40')
                        }
                        title={u.error ?? u.name}
                      >
                        {u.previewUrl ? (
                          <img src={u.previewUrl} alt={u.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-default/40 px-1 text-center text-[9px] text-muted">
                            <span className="truncate">{u.name}</span>
                          </div>
                        )}
                        {uploading && (
                          <div className="absolute inset-0 grid place-items-center bg-black/40 font-mono text-[10px] text-white">
                            {pct}%
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => cancelUpload(u.fileId)}
                          title={t('prof.thread.removeUpload')}
                          className="absolute right-0.5 top-0.5 grid h-4 w-4 place-items-center rounded-full bg-black/55 text-white transition-colors hover:bg-black/75"
                        >
                          <CloseIcon className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex items-end gap-1.5">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) startUploads(e.target.files);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  aria-label={t('prof.thread.attachImage')}
                  title={t('prof.thread.attachImage')}
                  onClick={() => fileInputRef.current?.click()}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted transition-colors hover:bg-default/50 hover:text-foreground disabled:opacity-50"
                >
                  <PaperclipIcon className="h-[18px] w-[18px]" />
                </button>
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    } else if (e.key === 'Escape' && replyingTo) {
                      e.preventDefault();
                      setReplyingTo(null);
                    }
                  }}
                  placeholder={t('prof.thread.composerPlaceholder')}
                  className="min-w-0 flex-1 rounded-lg border border-separator bg-default/40 px-3 py-2 text-sm text-ink outline-none placeholder:text-ink-muted focus:border-accent"
                />
                {(draft.trim().length > 0 || pendingUploads.length > 0) && (
                  <button
                    type="button"
                    onClick={() => void send()}
                    aria-label={t('prof.thread.send')}
                    title={t('prof.thread.send')}
                    className="grid h-9 w-9 shrink-0 animate-[send-pop_0.18s_ease-out] place-items-center rounded-full bg-primary text-white shadow-sm transition-transform hover:scale-105 active:scale-95 disabled:opacity-60"
                  >
                    <SendIcon className="h-[18px] w-[18px]" />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
